import { useSearchParams, Link } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import EventBooking from "@/components/event/EventBooking";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import EventDetailHeader from "@/components/event-detail/EventDetailHeader";
import EventDetailInfo from "@/components/event-detail/EventDetailInfo";
import { useEventDetail } from "@/components/event-detail/useEventDetail";
import { useEventBooking } from "@/hooks/useEventBooking";
import { getCampaignByCode } from "@/lib/campaigns";
import { useAuth } from "@/components/auth/AuthProvider";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { ClipboardCheck } from "lucide-react";
import DepartureSchedule from "@/components/event/DepartureSchedule";
import { earliestOpenGroup } from "@/lib/eventGroups";

const EventDetail = () => {
  const { toast } = useToast();
  const { event, isLoading, isGroupAvailabilityError, refetchGroupAvailability } = useEventDetail();
  const { isAuthenticated, userId } = useAuth();
  const { isAdmin, isLoading: adminLoading } = useIsAdmin();
  const [searchParams, setSearchParams] = useSearchParams();
  const [specialOfferCode, setSpecialOfferCode] = useState<string | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  
  // Read partner code from URL
  const urlPartnerCode = searchParams.get("partnerCode") || null;
  
  // Validate URL partner code
  const { data: urlPartnerCodeInfo } = useQuery({
    queryKey: ['url-partner-code', urlPartnerCode],
    queryFn: async () => {
      if (!urlPartnerCode) return null;
      const { data, error } = await supabase
        .from('partner_codes')
        .select('code, partner_name, discount_type, discount_percentage, credits_value, applies_to_tags, is_active, valid_from, valid_until, checkout_preview_text')
        .eq('code', urlPartnerCode)
        .eq('is_active', true)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!urlPartnerCode,
  });
  
  const { 
    handleBook, 
    isProcessing,
  } = useEventBooking(specialOfferCode, urlPartnerCode);

  // Fetch user's tags to check eligibility
  const { data: userTagAssignments } = useQuery({
    queryKey: ['user-tag-assignments-self'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from('user_tag_assignments')
        .select('tag:user_tags(name)')
        .eq('user_id', user.id);

      if (error) throw error;
      return data?.map((a: any) => a.tag?.name).filter(Boolean) || [];
    },
    enabled: isAuthenticated,
  });

  const { data: bookedGroupIds = [] } = useQuery({
    queryKey: ["user-ticket-groups", event?.id, userId],
    enabled: isAuthenticated && !!event?.id,
    queryFn: async () => {
      if (!event?.id || !userId) return [];
      const { data, error } = await supabase
        .from("tickets")
        .select("group_id")
        .eq("event_id", event.id)
        .eq("user_id", userId)
        .eq("status", "confirmed");
      if (error) throw error;
      return Array.from(new Set((data ?? []).map((ticket) => ticket.group_id).filter((id): id is string => !!id)));
    },
  });

  useEffect(() => {
    if (bookedGroupIds.length > 0 && event?.groups?.some((group) => group.group_id === bookedGroupIds[0])) {
      setSelectedGroupId(bookedGroupIds[0]);
    }
  }, [bookedGroupIds, event?.groups]);

  // Check if user has any of the required tags
  const requiredTags = event?.required_tags || [];
  const userTags = userTagAssignments || [];
  const matchedTag = requiredTags.find(rt => userTags.includes(rt));
  const userHasRequiredTag = requiredTags.length === 0 || Boolean(matchedTag);

  // Check if this is a Secret Salon event
  const isSecretSalon = event?.tags?.some(tag => tag.toLowerCase() === 'secret salon') || false;

  useEffect(() => {
    if (!event?.groups?.length) {
      setSelectedGroupId(null);
      return;
    }
    const requested = searchParams.get("group");
    const requestedGroup = event.groups.find((group) => group.group_id === requested && group.spots_remaining > 0);
    setSelectedGroupId((currentId) => {
      const current = event.groups?.find((group) => group.group_id === currentId && group.spots_remaining > 0);
      return (requestedGroup ?? current ?? earliestOpenGroup(event.groups ?? []))?.group_id ?? null;
    });
  }, [event?.id, event?.groups, searchParams]);
  
  // Check if user has invitation URL parameter
  const hasInvite = searchParams.get("invited") === "true";

  // Handle special offer and Stripe success/cancel
  useEffect(() => {
    if (!event) return;
    if (isProcessing) return; // already processing a booking

    // Handle special offer
    const specialOffer = searchParams.get("specialOffer");
    if (specialOffer && specialOffer !== specialOfferCode) {
      const campaign = getCampaignByCode(specialOffer);
      if (campaign && campaign.targetEventId === event.id) {
        setSpecialOfferCode(specialOffer);
        toast({
          title: "Special Offer Applied!",
          description: `${campaign.discountPercent}% discount has been applied to this event.`,
        });
      }
    }

    const success = searchParams.get("success");
    
    const isFailedPayment = success === "false";

    if (isFailedPayment) {
      toast({
        title: "Payment Canceled",
        description: "Your booking was not completed. Please try again.",
        variant: "destructive",
      });
      // Clear the params
      setSearchParams(new URLSearchParams(), { preventScrollReset: true });
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, event, isProcessing, specialOfferCode]);

  if (isLoading || !event) {
    return <LoadingSpinner />;
  }

  const now = new Date();
  const eventDate = new Date(event.start_time);
  const daysUntilEvent = Math.ceil(
    (eventDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
  );
  const isRefundable = daysUntilEvent > 3;

  // Check-in link visibility: event host, wave host, or admin.
  // Wave-only hosts must pass the same OR that EventCheckin.tsx uses.
  const isHost =
    event.host_id === userId ||
    event.secondary_host_id === userId ||
    (event.groups ?? []).some(
      (g) => !!userId && (g.host_id === userId || g.secondary_host_id === userId)
    );
  const showCheckinLink = (isHost || (!adminLoading && isAdmin)) && isAuthenticated;

  return (
    <div className="pb-32 animate-fade-in">
      <EventDetailHeader event={event}>
        {!!event.groups?.length && (
          <div className="mt-5">
            <DepartureSchedule
              event={event}
              groups={event.groups}
              selectedGroupId={selectedGroupId}
              onSelect={setSelectedGroupId}
              bookedGroupIds={bookedGroupIds}
            />
            {event.groups[0].spots_remaining <= 0 && earliestOpenGroup(event.groups) && (
              <p className="-mt-3 mb-4 text-sm text-muted-foreground">
                {event.groups[0].name} is full · Next available: {earliestOpenGroup(event.groups)?.name}
              </p>
            )}
          </div>
        )}
        {isGroupAvailabilityError && (
          <div className="mt-4 border-y border-border py-3">
            <p className="text-sm text-foreground">Departure availability couldn't be loaded.</p>
            <Button variant="link" className="h-auto p-0 text-sm" onClick={() => refetchGroupAvailability()}>Try again</Button>
          </div>
        )}
        {showCheckinLink && (
          <div className="mt-3">
            <Link to={`/events/${event.id}/checkin`}>
              <Button className="w-full gap-2 bg-secondary text-secondary-foreground hover:bg-secondary/90">
                <ClipboardCheck className="h-4 w-4" />
                Guest List & Check-in
              </Button>
            </Link>
          </div>
        )}
      </EventDetailHeader>

      <EventDetailInfo 
        event={event} 
        selectedGroupId={selectedGroupId}
        isRefundable={isRefundable} 
        hideWhatsApp 
        userHasRequiredTag={userHasRequiredTag}
        matchedTagName={matchedTag}
        isAuthenticated={isAuthenticated}
      />
      {!isGroupAvailabilityError && <EventBooking
        price={event.price}
        creditsCost={event.credit_cost}
        onBook={handleBook}
        isProcessing={isProcessing}
        event={event}
        isSecretSalon={isSecretSalon}
        hasInvite={hasInvite}
        userHasRequiredTag={userHasRequiredTag}
        matchedTagName={matchedTag}
        urlPartnerCode={urlPartnerCode}
        urlPartnerCodeInfo={urlPartnerCodeInfo}
        specialOfferCode={specialOfferCode}
        selectedGroupId={selectedGroupId}
        onSelectedGroupChange={setSelectedGroupId}
      />}
    </div>
  );
};

export default EventDetail;
